import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Standalone inline chat scoped to a specific `room` (e.g. "fm151", "vibe").
// Uses REST GET + WebSocket for live updates. Independent of the global drawer.
const StationChat = ({ room, title = "Live Chat" }) => {
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const [name, setName] = useState(() => localStorage.getItem("gfn_name") || "");
  const [text, setText] = useState("");
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const reconnectRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND}/api/chat/messages?room=${encodeURIComponent(room)}`);
      if (res.ok) setMessages(await res.json());
    } catch {}
  }, [room]);

  useEffect(() => {
    load();
  }, [load]);

  const connect = useCallback(() => {
    try {
      const ws = new WebSocket(BACKEND.replace(/^http/, "ws") + "/api/ws/chat");
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        if (reconnectRef.current) clearTimeout(reconnectRef.current);
        reconnectRef.current = setTimeout(connect, 3000);
      };
      ws.onmessage = (evt) => {
        try {
          const p = JSON.parse(evt.data);
          if (p.type === "chat" && p.data) {
            const msgRoom = p.data.room || null;
            if (msgRoom === room) {
              setMessages((prev) =>
                prev.some((m) => m.id === p.data.id) ? prev : [...prev, p.data]
              );
            }
          } else if (p.type === "delete" && p.data?.id) {
            setMessages((prev) => prev.filter((m) => m.id !== p.data.id));
          } else if (p.type === "moderation" && p.data?.reason) {
            toast.error(p.data.reason);
          }
        } catch {}
      };
    } catch {}
  }, [room]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connect]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Enter your name");
    if (!text.trim()) return;
    localStorage.setItem("gfn_name", name.trim());
    const payload = { name: name.trim(), message: text.trim(), room };
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "chat", ...payload }));
    } else {
      fetch(`${BACKEND}/api/chat/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then(async (r) => {
          if (r.status === 422) {
            const j = await r.json().catch(() => ({}));
            toast.error(j.detail || "Message blocked");
          }
        })
        .catch(() => {});
    }
    setText("");
  };

  return (
    <div
      data-testid={`station-chat-${room}`}
      className="bg-jet-surface border border-gold/25 rounded-lg overflow-hidden flex flex-col h-[500px]"
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-neutral-900">
        <div>
          <div className="font-condensed uppercase tracking-widest text-[10px] text-gold">
            {title}
          </div>
          <div className="font-condensed uppercase tracking-widest text-[9px] text-neutral-500">
            {connected ? "● Connected" : "○ Connecting…"}
          </div>
        </div>
        <span className="font-condensed uppercase tracking-widest text-[10px] text-neutral-500">
          #{room}
        </span>
      </header>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
        {messages.length === 0 ? (
          <div className="text-center text-neutral-500 text-sm py-16">
            Say something. Be the first in the room.
          </div>
        ) : (
          messages.map((m) => {
            const mine = name && m.name === name;
            return (
              <div
                key={m.id}
                data-testid={`chat-msg-${m.id}`}
                className={`rounded-md px-3 py-2 ${
                  mine
                    ? "bg-crimson/10 border-l-2 border-crimson ml-6"
                    : "bg-neutral-900/70 mr-6"
                }`}
              >
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`font-condensed uppercase tracking-widest text-[10px] ${mine ? "text-crimson" : "text-gold"}`}>
                    {m.name}
                  </span>
                  <span className="text-neutral-600 text-[10px]">
                    {new Date(m.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <div className="text-sm text-neutral-100 break-words">{m.message}</div>
              </div>
            );
          })
        )}
      </div>
      <form onSubmit={submit} className="px-3 py-3 border-t border-neutral-900 space-y-2">
        <input
          data-testid={`chat-name-${room}`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          maxLength={32}
          className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
        />
        <div className="flex gap-2">
          <input
            data-testid={`chat-input-${room}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type a message…"
            maxLength={500}
            className="flex-1 bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
          />
          <button
            data-testid={`chat-send-${room}`}
            type="submit"
            aria-label="Send"
            className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-gold hover:bg-gold-light text-black transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default StationChat;
